import * as THREE from 'three'
import Experience from '../Experience.js'
import gsap from 'gsap'

export default class Animasjoner {
    constructor() {
        this.experience = new Experience()
        this.instance = this.experience.camera.instance
        this.scene = this.experience.scene
        this.world = this.experience.world

        this.currentTimeline = null
        this.isPlaying = false
        this.hasStarted = false
        this.loopEnabled = true
        this.loopDelay = 1000

        this.progress = { t: 0 }
        this.lookTarget = new THREE.Vector3()

        this.model = null
        this.rørModel = null

        this.windows = []
        this.walls = []
        this.rør = []

        this.setCurve()
        this.waitForModelsAndStart()
    }

    waitForModelsAndStart() {
        const check = () => {
            if (this.hasStarted) return

            const ready = this.setupModels()

            if (!ready) {
                requestAnimationFrame(check)
                return
            }

            this.hasStarted = true
            this.startAutoPlay()
        }

        check()
    }

    setupModels() {
        this.model = this.world?.blokk?.model || null
        this.rørModel = this.world?.rør?.model || null

        this.windows = []
        this.walls = []
        this.rør = []

        if (!this.model || !this.rørModel) {
            console.warn('Animasjoner: model eller rørModel er ikke klar enda')
            return false
        }

        this.model.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) return

            if (child.name === 'vindu') {
                this.windows.push(child)
                if (child.material) {
                    child.material.transparent = true
                    child.material.opacity = child.material.opacity ?? 1
                }
            }

            if (child.material && child.material.name === 'panelPlaterStein') {
                this.walls.push(child)
                child.material.transparent = true
                child.material.opacity = child.material.opacity ?? 1
            }

            if (child.material && child.material.name === '[Formica Graystone]') {
                this.walls.push(child)
                child.material.transparent = true
                child.material.opacity = child.material.opacity ?? 1
            }
        })

        this.rørModel.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) return

            if (
                child.name === 'kv002' ||
                child.name === 'orasnje-vv-kv002' ||
                child.name === 'vv002'
            ) {
                this.rør.push(child)
                if (child.material) {
                    child.material.transparent = true
                    child.material.opacity = child.material.opacity ?? 1
                }
            }
        })

        return true
    }

    setCurve() {
        const center = new THREE.Vector3(3.20, 35, 100)
        const radius = 75
        const points = []
        const segments = 32

        for (let i = 0; i < segments; i++) {
            const angle = (i / segments) * Math.PI * 2
            const x = center.x + Math.cos(angle) * radius
            const z = center.z + Math.sin(angle) * radius
            points.push(new THREE.Vector3(x, center.y, z))
        }

        this.curve = new THREE.CatmullRomCurve3(points, true)
    }

    stopAll() {
        this.isPlaying = false

        if (this.currentTimeline) {
            this.currentTimeline.kill()
            this.currentTimeline = null
        }

        gsap.killTweensOf(this.progress)
        gsap.killTweensOf(this.lookTarget)
        gsap.killTweensOf(this.instance.position)

        this.windows.forEach((mesh) => {
            if (mesh.material) gsap.killTweensOf(mesh.material)
        })

        this.walls.forEach((mesh) => {
            if (mesh.material) gsap.killTweensOf(mesh.material)
        })

        this.rør.forEach((mesh) => {
            if (mesh.material) gsap.killTweensOf(mesh.material)
        })
    }

    startAutoPlay() {
    if (this.isPlaying) return

    this.isPlaying = true
    console.log('autoplay start')

    this.playOrbit(() => {
        if (!this.isPlaying) return
        console.log('orbit ferdig')

        this.playBlokk(() => {
            if (!this.isPlaying) return
            console.log('blokk ferdig')

            this.playTour(() => {
                if (!this.isPlaying) return
                console.log('tour ferdig')

                this.playOne(() => {
                    if (!this.isPlaying) return
                    console.log('one ferdig')

                    this.playTwo(() => {
                        if (!this.isPlaying) return
                        console.log('two ferdig')

                        this.playThree(() => {
                            if (!this.isPlaying) return
                            console.log('three ferdig')
                            console.log('sekvens ferdig')

                            this.currentTimeline = null
                            this.isPlaying = false

                            if (this.loopEnabled) {
                                setTimeout(() => {
                                    this.startAutoPlay()
                                }, this.loopDelay)
                            }
                        })
                    })
                })
            })
        })
    })
}

    addHideMaterials(tl, duration = 2) {
        this.windows.forEach((mesh) => {
            if (!mesh.material) return
            tl.to(mesh.material, {
                opacity: 0,
                duration,
                ease: 'power1.inOut'
            }, 0)
        })

        this.walls.forEach((mesh) => {
            if (!mesh.material) return
            tl.to(mesh.material, {
                opacity: 0.3,
                duration,
                ease: 'power1.inOut'
            }, 0)
        })

        this.rør.forEach((mesh) => {
            if (!mesh.material) return
            tl.to(mesh.material, {
                opacity: 0,
                duration: 1,
                ease: 'power1.inOut',
                onStart: () => {
                    mesh.material.depthWrite = false
                    mesh.material.depthTest = false
                }
            }, 0)
        })
    }

    addShowMaterials(tl, duration = 2) {
        this.windows.forEach((mesh) => {
            if (!mesh.material) return
            tl.to(mesh.material, {
                opacity: 1,
                duration,
                ease: 'power1.inOut'
            }, 0)
        })

        this.walls.forEach((mesh) => {
            if (!mesh.material) return
            tl.to(mesh.material, {
                opacity: 1,
                duration,
                ease: 'power1.inOut'
            }, 0)
        })

        this.rør.forEach((mesh) => {
            if (!mesh.material) return
            tl.to(mesh.material, {
                opacity: 1,
                duration,
                ease: 'power1.inOut',
                onStart: () => {
                    mesh.material.depthWrite = true
                    mesh.material.depthTest = true
                }
            }, 0)
        })
    }

    playOrbit(onDone = null) {
        console.log('playOrbit start')
        this.progress.t = 0.0

        const tl = gsap.timeline({
            onComplete: () => {
                console.log('playOrbit complete')
                this.currentTimeline = null
                if (onDone) onDone()
            }
        })

        this.currentTimeline = tl

        tl.to(this.progress, {
            t: 0.15,
            duration: 10,
            ease: 'none',
            onUpdate: () => {
                const point = this.curve.getPointAt(this.progress.t)
                this.instance.position.copy(point)
                this.instance.lookAt(3.20, 5, 94.52)
            }
        }, 0)

        this.addHideMaterials(tl, 2)
    }

    playBlokk(onDone = null) {
        console.log('playBlokk start')
        this.lookTarget.set(3.20, 5, 94.52)

        const tl = gsap.timeline({
            onStart: () => {
                this.instance.position.set(30.9, 2.5, 81.5)
                this.instance.lookAt(this.lookTarget)
            },
            onComplete: () => {
                console.log('playBlokk complete')
                this.currentTimeline = null
                if (onDone) onDone()
            }
        })

        this.currentTimeline = tl

        this.addShowMaterials(tl, 2)

        tl.to(this.instance.position, {
            x: 30.9,
            y: 2.5,
            z: 81.5,
            duration: 2,
            ease: 'none'
        }, 0)

        // tl.to(this.lookTarget, {
        //     x: 9.44,
        //     y: 3.17,
        //     z: 81.43,
        //     duration: 5,
        //     ease: 'none',
        //     onUpdate: () => {
        //         this.instance.lookAt(this.lookTarget)
        //     }
        // }, 0)

        // tl.to(this.lookTarget, {
        //     x: 11.58,
        //     y: 18,
        //     z: 68,
        //     duration: 3,
        //     ease: 'none',
        //     onUpdate: () => {
        //         this.instance.lookAt(this.lookTarget)
        //     }
        // })

        // tl.to(this.lookTarget, {
        //     x: 10.50,
        //     y: 18,
        //     z: 130,
        //     duration: 10,
        //     ease: 'none',
        //     onUpdate: () => {
        //         this.instance.lookAt(this.lookTarget)
        //     }
        // })

        // tl.to(this.lookTarget, {
        //     x: 10.50,
        //     y: 4,
        //     z: 133,
        //     duration: 5,
        //     ease: 'none',
        //     onUpdate: () => {
        //         this.instance.lookAt(this.lookTarget)
        //     }
        // })

        tl.to(this.lookTarget, {
            x: 9.44,
            y: 3.17,
            z: 81.43,
            duration: 2,
            ease: 'none',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        },0)
    }

    playTour(onDone = null) {
        console.log('playTour start')

        const currentDir = new THREE.Vector3()
        this.instance.getWorldDirection(currentDir)
        this.lookTarget.copy(this.instance.position).add(currentDir.multiplyScalar(10))

        const tl = gsap.timeline({
            onComplete: () => {
                console.log('playTour complete')
                this.currentTimeline = null
                if (onDone) onDone()
            }
        })

        this.currentTimeline = tl

        this.addShowMaterials(tl, 2)

        tl.to(this.instance.position, {
            x: 4.29,
            y: 2.5,
            z: 81.5,
            duration: 7.5,
            ease: 'sine.inOut'
        }, 0)
        tl.to(this.lookTarget, {
            x: 0.12,
            y: 2.88,
            z: 81.5,
            duration: 5,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        },0)

        tl.to(this.lookTarget, {
            x: 4.64,
            y: 2.88,
            z: 63,
            duration: 2.5,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        },'-=2')

        tl.to(this.instance.position, {
            x: 4.38,
            y: 2.5,
            z: 67,
            duration: 15,
            ease: 'power1.inOut'
        })

        tl.to(this.instance.position, {
            x: 3.78,
            y: 2.5,
            z: 128,
            duration: 40,
            ease: 'power1.inOut'
        })

        tl.to(this.lookTarget, {
            x: 3.78,
            y: 3.5,
            z: 133,
            duration: 5,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        },'<')

        tl.to(this.lookTarget, {
            x: 4.22,
            y: 3.1,
            z: 83.4,
            duration: 7.5,
            ease: 'none',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        })

        tl.to(this.instance.position, {
            x: 4,
            y: 2.5,
            z: 86,
            duration: 20,
            ease: 'power1.inOut'
        }, '-=2')

        tl.to(this.lookTarget, {
            x: 4.22,
            y: 3.5,
            z: 83.4,
            duration: 7.5,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, '<')
    }

    playOne(onDone = null) {
        console.log('playOne start')

        const currentDir = new THREE.Vector3()
        this.instance.getWorldDirection(currentDir)
        this.lookTarget.copy(this.instance.position).add(currentDir.multiplyScalar(10))

        const tl = gsap.timeline({
            onComplete: () => {
                console.log('playOne complete')
                this.currentTimeline = null
                if (onDone) onDone()
            }
        })

        this.currentTimeline = tl

        this.addShowMaterials(tl, 2)

        tl.to(this.instance.position, {
            x: 3.92,
            y: 3.05,
            z: 65.36,
            duration: 0.1,
            ease: 'power1.inOut'
        }, 0)

        tl.to(this.lookTarget, {
            x: 4.11,
            y: 3.15,
            z: 64.33,
            duration: 0.1,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, 0)

        tl.to(this.instance.position, {
            x: 5.04,
            y: 2.42,
            z: 65.01,
            duration: 15,
            ease: 'power1.inOut'
        })

        tl.to(this.lookTarget, {
            x: 4.62,
            y: 3.26,
            z: 63.73,
            duration: 15,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, '<')
    }

    playTwo(onDone = null){
        console.log('playTwo start')

        const currentDir = new THREE.Vector3()
        this.instance.getWorldDirection(currentDir)
        this.lookTarget.copy(this.instance.position).add(currentDir.multiplyScalar(10))

        const tl = gsap.timeline({
            onComplete: () => {
                console.log('playTwo complete')
                this.currentTimeline = null
                if (onDone) onDone()
            }
        })

        this.currentTimeline = tl

        this.addShowMaterials(tl, 2)

        tl.to(this.instance.position, {
            x: 4.78,
            y: 3.17,
            z: 88.74,
            duration: 0.1,
            ease: 'power1.inOut'
        }, 0)

        tl.to(this.lookTarget, {
            x: 4.68,
            y: 3.19,
            z: 89.22,
            duration: 0.1,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, 0)

        tl.to(this.instance.position, {
            x: 4.87,
            y: 2.89,
            z: 87.87,
            duration: 15,
            ease: 'power1.inOut'
        })

        tl.to(this.lookTarget, {
            x: 4.45,
            y: 3.27,
            z: 89.32,
            duration: 15,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, '<')
    }

    playThree(onDone = null){
        console.log('playThree start')

        const currentDir = new THREE.Vector3()
        this.instance.getWorldDirection(currentDir)
        this.lookTarget.copy(this.instance.position).add(currentDir.multiplyScalar(10))

        const tl = gsap.timeline({
            onComplete: () => {
                console.log('playThree complete')
                this.currentTimeline = null
                if (onDone) onDone()
            }
        })

        this.currentTimeline = tl

        this.addShowMaterials(tl, 2)

        tl.to(this.instance.position, {
            x: 4.28,
            y: 3.21,
            z: 107.74,
            duration: 0.1,
            ease: 'power1.inOut'
        }, 0)

        tl.to(this.lookTarget, {
            x: 4.52,
            y: 3.23,
            z: 107.57,
            duration: 0.1,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, 0)

        tl.to(this.instance.position, {
            x: 4.30,
            y: 3.09,
            z: 107.34,
            duration: 15,
            ease: 'power1.inOut'
        })

        tl.to(this.lookTarget, {
            x: 4.53,
            y: 3.23,
            z: 107.54,
            duration: 15,
            ease: 'sine.inOut',
            onUpdate: () => {
                this.instance.lookAt(this.lookTarget)
            }
        }, '<')
    }
}